// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file userNormalizer.js
 * @description Utilidades para normalizar el modelo de usuario — Single source: profile_avatar_full_url
 * @version 5.0.0
 *
 * Avatar: delegado a resolveAvatar (avatarResolver.js). Prioridad alineada con pickCanonicalAvatarSourceUrl.
 */
import { resolveAvatar, pickCanonicalAvatarSourceUrl } from './avatarResolver';

/**
 * Normaliza el modelo de usuario a un formato consistente
 * @param {Object} userData - Datos del usuario desde API o localStorage
 * @returns {Object} Usuario normalizado (solo profile_avatar_full_url para imagen)
 */
export const normalizeUser = (userData) => {
  if (!userData) return null;

  let profileAvatarFullUrl = null;
  const canon = pickCanonicalAvatarSourceUrl(userData);
  if (canon && typeof canon === 'string') {
    const trimmed = canon.trim();
    if (trimmed && !trimmed.startsWith('data:image/')) profileAvatarFullUrl = trimmed;
  }
  const avatarCanonical = profileAvatarFullUrl || null;

  const normalized = {
    id: userData.id || userData.usuario_id || null,
    usuario_id: userData.usuario_id || userData.id || null,
    profile_avatar_full_url: profileAvatarFullUrl,
    profile_avatar_url: avatarCanonical,
    avatar_url: avatarCanonical,
    avatar: avatarCanonical,
    foto_perfil: avatarCanonical,
    avatar_version: userData.avatar_version ?? null,
    nick: userData.nick || userData.username || null,
    nombre_real: userData.nombre_real || userData.nombre || null,
    apellido: userData.apellido || null,
    email: userData.email || null,
    telefono: userData.telefono || null,
    biografia: userData.biografia || null,
    idioma_preferido: userData.idioma_preferido || 'es',
    codigo_pais: userData.codigo_pais || 'AR',
    zona_horaria: userData.zona_horaria || 'America/Argentina/Buenos_Aires',
    theme: (userData.theme && ['light', 'dark', 'argentina'].includes(String(userData.theme).toLowerCase()))
      ? (String(userData.theme).toLowerCase() === 'light' ? 'argentina' : String(userData.theme).toLowerCase())
      : 'argentina',
    plan: (userData.plan || 'free').toLowerCase(),
    plan_code: (userData.plan_code || userData.plan || 'free').toLowerCase(),
    is_founder: Number(userData.is_founder || 0),
    founder_number: userData.founder_number ?? null,
    founder_position: userData.founder_position != null ? Number(userData.founder_position) : null,
    founder_limit: userData.founder_limit != null ? Number(userData.founder_limit) : null,
    notification_sound_enabled:
      Number(userData.notification_sound_enabled ?? 1) === 0 ? 0 : 1,
    unread_email_enabled: Number(userData.unread_email_enabled ?? 0) === 1 ? 1 : 0,
    unread_email_delay_hours: Math.min(
      168,
      Math.max(1, Number(userData.unread_email_delay_hours ?? 24) || 24)
    ),
    plan_since: userData.plan_since || userData.fecha_registro || null,
    fecha_registro: userData.fecha_registro || null,
    visibility: userData.visibility || 'public',
    status: userData.status || 'offline',
  };

  return normalized;
};

/**
 * URL de avatar con cache busting. Delega a resolveAvatar (prioridad: profile_avatar_full_url → profile_avatar_url → avatar → default).
 * @param {Object} user - Usuario
 * @returns {string} URL con ?v=avatar_version (nunca null; usa default si no hay URL)
 */
export const getUserAvatarUrl = (user) => resolveAvatar(user ?? {});

/**
 * Normaliza avatar a src válida para <img src={...}> con cache busting. Delega a resolveAvatar + basePath.
 * @param {Object} user - user o profile
 * @param {string} basePath - base path del front (ej. getBasePath())
 * @returns {string|null} src lista para img o null si no hay user
 */
export function getAvatarSrcForDisplay(user, basePath = '') {
  if (!user) return null;
  const url = resolveAvatar(user);
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) return url;
  const base = (basePath || '').replace(/\/?$/, '');
  return base ? `${base}${url.startsWith('/') ? url : '/' + url.replace(/^\//, '')}` : url;
}

/**
 * Avatar de contacto con cache busting. Delega a resolveAvatar.
 * @param {Object} contact - Objeto contacto
 * @returns {string} URL con ?v=avatar_version (nunca null; usa default si no hay URL)
 */
export function getContactAvatarUrl(contact) {
  return resolveAvatar(contact ?? {});
}

/**
 * Limpia el user para localStorage. Solo profile_avatar_full_url (sin base64).
 * @param {Object} user
 * @returns {Object}
 */
export function cleanUserForStorage(user) {
  if (!user || typeof user !== 'object') return user ?? {};
  const cleaned = { ...user };
  if (cleaned.profile_avatar_full_url && cleaned.profile_avatar_full_url.startsWith('data:image/')) cleaned.profile_avatar_full_url = null;
  return cleaned;
}
